import {ISO_DAY_LABELS, isoDayOf} from '../helpers/availability'

interface Props {
  dayOfWeek: number
  blocked: boolean
  disabled?: boolean
  onToggle: (dayOfWeek: number) => void
  /**
   * Dated mode: the real date this column stands for. The weekday name is
   * then read off the date rather than the column index, because the columns
   * follow the company's week start and need not begin on a Monday.
   */
  date?: Date
}

/**
 * A day column's header: the weekday name plus its day-off toggle.
 *
 * This was a bare "All day" checkbox, which said neither what it would do nor
 * what it had done — "all day" names no state, and at checkbox size it barely
 * read as interactive. It is now a labelled button naming the outcome
 * ("Mark day off" → "Day off"), because blocking a day replaces that whole
 * column's contents with one full-span slot and that is not an action to leave
 * ambiguous. The weekday name goes danger-coloured with it, so a blocked day is
 * legible from the header alone without decoding the column's fill.
 */
export const AvailabilityDayHeader = ({dayOfWeek, blocked, disabled, onToggle, date}: Props) => {
  const label = ISO_DAY_LABELS[date ? isoDayOf(date) : dayOfWeek]
  return (
    <div className='d-flex flex-column align-items-center justify-content-center py-2 px-1'>
      <span className={`fw-bold fs-6 lh-1 ${blocked ? 'text-danger' : 'text-gray-900'}`}>
        <span className='d-none d-md-inline'>{label}</span>
        <span className='d-md-none'>{label.slice(0, 3)}</span>
      </span>
      {date && (
        // The date is what distinguishes one Monday from the next, so it is
        // not decoration — it goes under the name at full legibility.
        <span
          className='fs-8 fw-semibold text-gray-600 lh-1 mt-1'
          data-testid={`availability-column-date-${dayOfWeek}`}
        >
          {date.toLocaleDateString(undefined, {day: 'numeric', month: 'short'})}
        </span>
      )}
      <button
        type='button'
        // A genuine two-state toggle, unlike the paint palette's radiogroup.
        aria-pressed={blocked}
        aria-label={
          blocked
            ? `${label} is marked unavailable all day. Activate to clear it.`
            : `Mark all of ${label} unavailable`
        }
        title={
          blocked
            ? `${label} is unavailable all day — click to clear`
            : `Mark all of ${label} unavailable`
        }
        data-testid={`availability-day-toggle-${dayOfWeek}`}
        disabled={disabled}
        onClick={() => onToggle(dayOfWeek)}
        // Idle state gets a dashed outline rather than btn-light: the header
        // cell is itself bg-light, so a light button on it has no edge and
        // reads as static text — which is what made the old checkbox invisible.
        // Dashed also conventionally signals "optional action not yet taken".
        className={`btn btn-sm d-inline-flex align-items-center mt-2 py-1 px-3 ${
          blocked
            ? 'btn-danger'
            : 'btn-outline btn-outline-dashed btn-active-light-danger text-gray-700'
        }`}
      >
        <i
          className={`bi ${blocked ? 'bi-x-circle-fill' : 'bi-slash-circle'} fs-8 me-2`}
          aria-hidden='true'
        />
        <span className='fs-8 fw-semibold text-nowrap'>{blocked ? 'Day off' : 'Mark day off'}</span>
      </button>
    </div>
  )
}
